import {
    Box,
    Button,
    Heading,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useInstitutionsContext } from 'contexts/institutions'
import { motion } from 'framer-motion'
import { BottonLine, FormContainer } from 'pages/Report/styles'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
    getDetranPrBillingReport,
    getDetranPrCollectionLetter,
    getDetranPrIssuedBillsReport,
    getDetranPrMonthlyBillStatusReport,
    getDetranRnDetailedDebtReport
} from 'services/report'
import { generateBlobFile } from 'utils/blob'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { FormDetranBillingReportValue } from './form'
import { schema } from './schema'

type ReportAction =
    | 'OFICIO_COBRANCA'
    | 'COBRANCA'
    | 'SITUACAO_BOLETOS_MENSAL'
    | 'BOLETOS_EMITIDOS'
    | 'RN_DETALHADO'

interface FormDetranBillingReport {
    uf: string
    periodoInicio: string
    periodoFim: string
    identificadorDebito: string
}

const reportOptions: Array<{ label: string; value: ReportAction }> = [
    {
        label: 'Ofício de Cobrança',
        value: 'OFICIO_COBRANCA'
    },
    {
        label: 'Relatório de Cobrança',
        value: 'COBRANCA'
    },
    {
        label: 'Relatório de Situação de Boletos Mensal',
        value: 'SITUACAO_BOLETOS_MENSAL'
    },
    {
        label: 'Relatório de Boletos Emitidos',
        value: 'BOLETOS_EMITIDOS'
    }
]

const rnReportOptions: Array<{ label: string; value: ReportAction }> = [
    { label: 'Listar débito detalhado', value: 'RN_DETALHADO' }
]

export const DetranBillingReports: React.FC = () => {
    const {
        register,
        formState: { errors },
        watch
    } = useForm<FormDetranBillingReport>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            uf: 'PR',
            periodoInicio: '',
            periodoFim: '',
            identificadorDebito: ''
        }
    })

    const { currentAgent } = useInstitutionsContext()
    const [loadingReport, setLoadingReport] = useState(false)
    const toast = useToast()

    const all = watch()

    const form = useMemo(() => FormDetranBillingReportValue(), [])

    const callAPI = async (action: ReportAction) => {
        if (all.uf === 'RN' &&
            (!/^\d+$/.test(all.identificadorDebito || '') ||
                Number(all.identificadorDebito) <= 0 ||
                Number(all.identificadorDebito) > 2147483647)) {
            toast({
                title: 'Identificador do débito obrigatório',
                description: 'Informe um identificador numérico entre 1 e 2147483647.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return
        }

        if (all.uf === 'PR' && !currentAgent?.cnpj) {
            toast({
                title: 'Agente Financeiro obrigatório',
                description:
                    'Selecione um Agente Financeiro no cabeçalho para gerar o relatório.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return
        }

        const periodoInicioOficio = all.periodoInicio?.slice(0, 7)
        const periodoFimOficio = all.periodoFim?.slice(0, 7)

        if (
            action === 'OFICIO_COBRANCA' &&
            periodoInicioOficio &&
            periodoFimOficio &&
            periodoInicioOficio !== periodoFimOficio
        ) {
            toast({
                title: 'Competência inválida',
                description:
                    'Para gerar o Ofício de Cobrança, os períodos inicial e final devem pertencer ao mesmo mês.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
            return
        }

        const competenciaOficio = all.periodoFim || all.periodoInicio
        const [anoOficio, mesOficio] = competenciaOficio
            ? competenciaOficio.split('-')
            : [undefined, undefined]

        const query = removeEmptyValues({
            uf: all.uf,
            cnpjAgenteFinanceiro: currentAgent?.cnpj?.replace(/\D/g, ''),
            ...(action === 'OFICIO_COBRANCA'
                ? {
                      ano: anoOficio,
                      mes: mesOficio
                  }
                : {
                      periodoInicio: all.periodoInicio,
                      periodoFim: all.periodoFim
                  })
        })

        setLoadingReport(true)

        try {
            let response

            if (action === 'RN_DETALHADO') {
                response = await getDetranRnDetailedDebtReport(Number(all.identificadorDebito))
            } else if (action === 'OFICIO_COBRANCA') {
                response = await getDetranPrCollectionLetter(query)
            } else if (action === 'COBRANCA') {
                response = await getDetranPrBillingReport(query)
            } else if (action === 'SITUACAO_BOLETOS_MENSAL') {
                response = await getDetranPrMonthlyBillStatusReport(query)
            } else {
                response = await getDetranPrIssuedBillsReport(query)
            }

            generateBlobFile(response, action === 'RN_DETALHADO'
                ? `debito-detalhado-rn-${all.identificadorDebito}.pdf`
                : 'relatorio-detran-pr.pdf')

            toast({
                title: 'Arquivo gerado com sucesso!',
                description:
                    'O relatório foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.',
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error: any) {
            toast({
                title: 'Sentimos muito!',
                description: await getErrorMessage(error),
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingReport(false)
        }
    }

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                paddingBottom="24px"
            >
                <Heading size="md">Relatórios de Cobrança</Heading>

                <Menu>
                    <MenuButton
                        as={Button}
                        colorScheme="primary"
                        isLoading={loadingReport}
                        isDisabled={all.uf === 'PR' && !currentAgent?.cnpj}
                    >
                        Gerar relatório
                    </MenuButton>
                    <MenuList>
                        {(all.uf === 'RN' ? rnReportOptions : reportOptions).map(option => (
                            <MenuItem
                                key={option.value}
                                onClick={() => callAPI(option.value)}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </MenuList>
                </Menu>
            </Box>

            <form>
                <Box
                    paddingTop="24px"
                    bgColor="white"
                    borderRadius={6}
                    h="100%"
                >
                    <Layout type="container" px={8} full display="grid" gap={8}>
                        <FormContainer
                            minH={{
                                base: 'calc(100vh - 310px)',
                                md: 'calc(100vh - 260px)'
                            }}
                        >
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {form.filter(({ input }) => all.uf === 'RN'
                                    ? !input || input.id === 'identificadorDebito'
                                    : input?.id !== 'identificadorDebito'
                                ).map(({ layout, input, select }, key) => (
                                    <Layout {...layout} key={key}>
                                        {select ? (
                                            <SelectBuild
                                                {...select}
                                                err={errors[select.id]}
                                                {...register(select.id)}
                                                value={watch(select.id)}
                                            />
                                        ) : (
                                            input && (
                                                <InputBuild
                                                    {...input}
                                                    err={errors[input.id]}
                                                    {...register(input.id)}
                                                    value={watch(input.id)}
                                                />
                                            )
                                        )}
                                    </Layout>
                                ))}

                        

                                <Box width="100%">
                                    <BottonLine />
                                </Box>
                            </Layout>
                        </FormContainer>
                    </Layout>
                </Box>
            </form>
        </Box>
    )
}

const getErrorMessage = async (error: any): Promise<string> => {
    const defaultMessage = 'Ocorreu um erro, tente novamente mais tarde.'

    if (!error?.response) {
        return defaultMessage
    }

    const data = error.response.data

    if (data instanceof Blob) {
        try {
            const text = await data.text()
            const parsed = JSON.parse(text)
            return (
                parsed?.erros?.[0]?.mensagem ??
                parsed?.message ??
                parsed?.title ??
                defaultMessage
            )
        } catch {
            return defaultMessage
        }
    }

    return (
        data?.erros?.[0]?.mensagem ??
        data?.message ??
        data?.title ??
        defaultMessage
    )
}
